import {
  PERMISSION_ACTION_ADD,
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_ORGANIZATION,
  PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE,
  PERMISSION_ENTITY_PURCHASE_ORDER,
  PERMISSION_ENTITY_SOURCE_DOCUMENT
} from '@klear/constants'
import toPairs from 'lodash/toPairs'

export const DOCUMENT_PROPOSAL_KINDS = [
  'purchase_order',
  'invoice',
  'operational',
  'payable',
  'proof_of_acceptance',
  'attachment',
  'information_request',
  'trader_document'
] as const
export type DocumentProposalKind = (typeof DOCUMENT_PROPOSAL_KINDS)[number]

export const FILING_IN_PROGRESS_MESSAGE = 'Another filing for this trader is in progress; try again in a moment.'

export const TRADER_SIDES = ['sells', 'buys'] as const
export type TraderSide = (typeof TRADER_SIDES)[number]

export const PRINTED_DOCUMENT_KINDS = [
  'purchase_order',
  'invoice',
  'credit_or_debit_memo',
  'change_to_existing_record',
  'delivery_or_acceptance',
  'something_else'
] as const
export type PrintedDocumentKind = (typeof PRINTED_DOCUMENT_KINDS)[number]

export const ORDER_RECORD_DOCUMENT_KINDS: Partial<Record<string, PrintedDocumentKind>> = {
  [PERMISSION_ENTITY_PURCHASE_ORDER]: 'purchase_order',
  [PERMISSION_ENTITY_INVOICE]: 'invoice',
  [PERMISSION_ENTITY_PROOF_OF_ACCEPTANCE]: 'delivery_or_acceptance'
}

const HINTS_THAT_NAME_NO_RECORD = new Set(['traderSide', 'documentKind'])

function namesRecord(hints: Readonly<Record<string, unknown>> | null | undefined): boolean {
  return toPairs(hints ?? {}).some(([key, value]) => value != null && !HINTS_THAT_NAME_NO_RECORD.has(key))
}

export function canSendSourceDocuments(
  user: { hasPermission: (entity: string, action: string) => boolean },
  hints:
    | (Readonly<Record<string, unknown>> & {
        traderSide?: TraderSide
        documentKind?: PrintedDocumentKind
        orderId?: number
      })
    | null
    | undefined
): boolean {
  if (user.hasPermission(PERMISSION_ENTITY_SOURCE_DOCUMENT, PERMISSION_ACTION_ADD)) return true
  if (!namesRecord(hints)) return user.hasPermission(PERMISSION_ENTITY_ORGANIZATION, PERMISSION_ACTION_ADD)
  if (hints?.orderId == null || hints.traderSide !== 'sells') return false
  return toPairs(ORDER_RECORD_DOCUMENT_KINDS).some(
    ([entity, kind]) => kind === hints.documentKind && user.hasPermission(entity, PERMISSION_ACTION_ADD)
  )
}

export const PROPOSAL_KINDS_BY_TRADER_SIDE: Record<TraderSide, readonly DocumentProposalKind[]> = {
  sells: ['purchase_order', 'invoice', 'proof_of_acceptance'],
  buys: ['payable']
}

export const REVIEWER_HINT_LIMITS = {
  reference: 200,
  value: 4000,
  field: 64,
  message: 1000,
  answer: 1000,
  note: 1000,
  confirmed: 200,
  answers: 100
} as const

export const DOCUMENT_RECORD_FIELD_NAMES = [
  'number',
  'title',
  'currency',
  'amount',
  'incoterm',
  'effectiveDate',
  'endDate',
  'notes'
] as const
export type DocumentRecordFieldName = (typeof DOCUMENT_RECORD_FIELD_NAMES)[number]

export const RECORD_FIELDS_BY_KIND: Record<DocumentProposalKind, readonly DocumentRecordFieldName[]> = {
  purchase_order: ['number', 'amount', 'incoterm', 'effectiveDate', 'endDate', 'notes'],
  invoice: ['number', 'amount', 'effectiveDate', 'endDate', 'notes'],
  operational: ['title', 'number', 'currency', 'amount', 'incoterm', 'effectiveDate', 'endDate', 'notes'],
  payable: ['number', 'amount', 'endDate', 'notes'],
  proof_of_acceptance: ['effectiveDate', 'notes'],
  attachment: [],
  information_request: [],
  trader_document: []
}

export const REQUIRED_RECORD_FIELDS: Partial<Record<DocumentProposalKind, readonly DocumentRecordFieldName[]>> = {
  purchase_order: ['amount', 'effectiveDate', 'endDate'],
  invoice: ['amount', 'effectiveDate', 'endDate'],
  operational: ['title', 'effectiveDate'],
  payable: ['amount', 'endDate'],
  proof_of_acceptance: ['effectiveDate']
}

export const PARENT_KIND_BY_RECORD_KIND: Partial<Record<DocumentProposalKind, DocumentProposalKind>> = {
  invoice: 'purchase_order',
  operational: 'purchase_order',
  proof_of_acceptance: 'invoice'
}

export const PARENT_REQUIRED_KINDS = ['proof_of_acceptance'] as const satisfies readonly DocumentProposalKind[]

export function isParentRequiredKind(kind: string): kind is (typeof PARENT_REQUIRED_KINDS)[number] {
  return (PARENT_REQUIRED_KINDS as readonly string[]).includes(kind)
}

export const RECORD_PROPOSAL_KINDS = [
  'purchase_order',
  'invoice',
  'operational',
  'payable',
  'proof_of_acceptance'
] as const satisfies readonly DocumentProposalKind[]

export const MOVABLE_RECORD_KINDS = ['invoice', 'operational'] as const satisfies readonly DocumentProposalKind[]

export function isMovableRecordKind(kind: string): kind is (typeof MOVABLE_RECORD_KINDS)[number] {
  return (MOVABLE_RECORD_KINDS as readonly string[]).includes(kind)
}

export const RECORD_UPDATE_FIELDS_BY_KIND = {
  purchase_order: ['number', 'incoterm', 'effectiveDate', 'endDate', 'notes', 'amount'],
  invoice: ['number', 'effectiveDate', 'endDate', 'notes']
} as const satisfies Partial<Record<DocumentProposalKind, readonly DocumentRecordFieldName[]>>

export type RecordUpdateFieldName =
  (typeof RECORD_UPDATE_FIELDS_BY_KIND)[keyof typeof RECORD_UPDATE_FIELDS_BY_KIND][number]

export const RECORD_UPDATE_FIELDS_BY_TYPE: Partial<Record<string, readonly RecordUpdateFieldName[]>> =
  RECORD_UPDATE_FIELDS_BY_KIND

export const CONTRIBUTION_FIELD_NAMES = ['amount', 'effectiveDate', 'endDate'] as const
export type ContributionFieldName = (typeof CONTRIBUTION_FIELD_NAMES)[number]

export const CONTRIBUTION_AMOUNT_KINDS = ['total', 'change'] as const
export type ContributionAmountKind = (typeof CONTRIBUTION_AMOUNT_KINDS)[number]
